/**
 * Utility functions for node type detection and manipulation
 */

/**
 * Get file extension from path or label
 * @param {string} path - File path
 * @param {string} label - Node label (fallback)
 * @returns {string} File extension (lowercase, without dot)
 */
export function getFileExtension(path, label) {
  const fileName = path || label || '';
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1 || lastDot === fileName.length - 1) {
    return '';
  }
  return fileName.substring(lastDot + 1).toLowerCase();
}

/**
 * Split filename into base name and extension
 * @param {string} fileName - File name
 * @returns {Object} Object with baseName and extension properties
 */
export function splitFileName(fileName) {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1 || lastDot === fileName.length - 1) {
    return { baseName: fileName, extension: '' };
  }
  return {
    baseName: fileName.substring(0, lastDot),
    extension: fileName.substring(lastDot)
  };
}

/**
 * Determine if a node is a class based on various indicators
 * @param {Object} data - Node data object
 * @param {string} id - Node ID
 * @returns {boolean} True if node is a class
 */
export function isClassNode(data, id) {
  const originalNodeType = data?.originalNode?.type;
  const isClassFromPath = data?.path && data.path.endsWith('.class');
  const isClassFromId = id && typeof id === 'string' && id.includes('.class');
  return originalNodeType === 'class' || isClassFromPath || isClassFromId;
}

/**
 * Determine if a node is a method based on various indicators
 * @param {Object} data - Node data object
 * @param {string} id - Node ID
 * @returns {boolean} True if node is a method
 */
export function isMethodNode(data, id) {
  const originalNodeType = data?.originalNode?.type;
  const isMethodFromPath = data?.path && data.path.endsWith('.method');
  const isMethodFromId = id && typeof id === 'string' && id.includes('.method');
  return originalNodeType === 'method' || isMethodFromPath || isMethodFromId;
}

/**
 * Determine if a node is a function based on various indicators
 * @param {Object} data - Node data object
 * @param {string} id - Node ID
 * @returns {boolean} True if node is a function
 */
export function isFunctionNode(data, id) {
  const originalNodeType = data?.originalNode?.type;
  const isFunctionFromPath = data?.path && data.path.endsWith('.function');
  const isFunctionFromId = id && typeof id === 'string' && id.includes('.function');
  return originalNodeType === 'function' || isFunctionFromPath || isFunctionFromId;
}

/**
 * Determine if children are all functions/methods/classes
 * @param {Array} children - Array of child nodes
 * @returns {boolean} True if all children are functions, methods, or classes
 */
export function areChildrenAllCodeElements(children) {
  if (!children || !Array.isArray(children) || children.length === 0) {
    return false;
  }
  
  return children.every(child => {
    const childType = child.type || (child.originalNode && child.originalNode.type);
    return childType === 'function' || childType === 'method' || childType === 'class';
  });
}

/**
 * Get display label for a node (removes .class or .method suffixes)
 * @param {string} label - Original label
 * @param {boolean} isClass - Whether node is a class
 * @param {boolean} isMethod - Whether node is a method
 * @returns {string} Display label
 */
export function getDisplayLabel(label, isClass, isMethod) {
  if (!label) return '';
  
  if (isClass && label.endsWith('.class')) {
    return label.substring(0, label.length - 6); // Remove '.class'
  }
  
  if (isMethod && label.endsWith('.method')) {
    // Remove .method suffix and class prefix
    const withoutMethodSuffix = label.substring(0, label.length - 7); // Remove '.method'
    const lastDotIndex = withoutMethodSuffix.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      return withoutMethodSuffix.substring(lastDotIndex + 1);
    }
    return withoutMethodSuffix;
  }
  
  return label;
}
